import {
	Dialog,
	renderReactDialog,
	Select,
	SelectItem,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import {
	getShareSettings,
	setShareSettings,
	SharedPermission,
	SharedSetting,
	SharedSettingsSaveModel
} from "client/Api/Documents/SharePoint/sharePointSharing";
import { ReactElement, useEffect, useState } from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { HttpRequestError } from "client/Api/httpClient";
import styles from "./ManagePermissionsDialog.module.less";
import { setTrackingId } from "client/Misc/utils";

const permissionOptions: SelectItem<SharedPermission>[] = [
	{ value: SharedPermission.CanView, text: localize("_CanView") },
	{ value: SharedPermission.CanEdit, text: localize("_CanEdit") },
	{ value: SharedPermission.StopSharing, text: localize("_StopSharing") },
];

export type ManagePermissionsDialogProps = {
	onClose: () => void;
	document: DocumentItemViewModel;
};

export const ManagePermissionsDialog = (
	props: ManagePermissionsDialogProps
): ReactElement => {
	const { onClose, document } = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formValue, setFormValue] = useState<SharedSettingsSaveModel>({
		settings: [],
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<SharedSettingsSaveModel> = useFormHandler(
		formValue,
		setFormValue,
		"NoValidation"
	);

	const submitHandler = async (): Promise<void> => {
		try {
			// when there is no role to update just close the dialog
			if (formValue.settings.length === 0) {
				onClose();
				return;
			}

			setIsBusy(true);

			await setShareSettings(
				document.id,
				convertToLocationModel(document.location),
				formValue,
				globalCancellation
			);

			setIsBusy(false);
			onClose();
			alertManager.showSuccess(localize("_PermissionsHaveBeenChanged"));
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			setIsBusy(false);

			if (formHandler.validation.tryProcessServerError(e)) {
				return;
			}

			alertManager.showError(localize("_ErrorOfChangingSharedSettings"));
		}
	};

	useEffect(
		() => {
			async function fetchShareSettings(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const settings: SharedSetting[] = await getShareSettings(
						document.id,
						document.location,
						signal
					);
					setIsBusy(false);
					setFormValue({ settings });
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					const alertMessageKey: LocalizationKey =
						e instanceof HttpRequestError && e.statusCode === 403
							? "_YouDoNotHavePermissionForThat"
							: "_GeneralErrorMessage";

					alertManager.showError(localize(alertMessageKey));
					onClose();
				}
			}

			const cancellation = new AbortController();
			fetchShareSettings(cancellation.signal);

			return () => {
				cancellation.abort();
			};
		},
		[
			alertManager,
			onClose,
			document
		]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="manage-document-permissions-dialog"
		>
			<Dialog.Header
				title={localize("_WhoHasAccess")}
				onClose={onClose}
			/>
			<Dialog.Body>
				{formValue.settings.length > 0 ? (
					<div className={styles.content}>
						{formHandler.for("settings").map((settingHandler) => (
							<div
								key={
									settingHandler.value
										.member
										.id
								}
								className={styles.sharedSettingContainer}
							>
								<span className={styles.memberName}>
									{
										settingHandler.value
											.member
											.name
									}
								</span>
								<div className={styles.dropdownContainer}>
									<Select
										{...settingHandler.for("permission")
											.attachToSelect()}
										options={permissionOptions}
										isDisabled={
											!settingHandler.value
												.enabled
										}
										menuTitle={localize("_Permissions")}
									/>
								</div>
							</div>
						))}
					</div>
				) : (
					<div {...setTrackingId("no-item-to-show")}>
						{localize("_NoItemsToShow")}
					</div>
				)}
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Done")}
						onClick={submitHandler}
						trackingId="done"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

ManagePermissionsDialog.displayName = "ManagePermissionsDialog";

export function openManagePermissionsDialog(
	props: Omit<ManagePermissionsDialogProps, "onClose">
): void {
	renderReactDialog((onClose) => (
		<ManagePermissionsDialog {...props} onClose={onClose} />
	));
}
